import { useContext, useEffect, useMemo, useState } from 'react';
import LoadingButton from './LoadingButton';
import { ShopifyCustomerAddress } from '../interface/interface';
import loading from '../assets/loading.svg';
import { UserContext } from '../context/userContext';
import { AddressAutocomplete } from './AddressAutocomplete';
import { states } from '../constant/constant';
import { getShippingAddress } from '../utils/formatAddress';
import showToast from './ToastComponent';
import { CloseIcon } from '../assets/svgImages';
import CustomModal from './CustomModal';
import { deleteShippingAddress } from '../service';
import { useAuth } from 'react-oidc-context';
import Loading from './Loading';
import { callCustomerAddresses } from '../service/callCustomerAddresses';
import { sort } from '../utils/sort';

const fixNullAddressString = (addressString?: string | null) => {
  return addressString && addressString !== '' ? `, ${addressString}` : '';
};

const ShippingDetails = (props: {
  cart: any;
  addressList: any[];
  isAddressListFetching: boolean;
  addShipping: (
    shippingDetails: ShopifyCustomerAddress,
  ) => Promise<boolean | void> | void;
  editShippingEnabled: boolean;
  isButtonLoadingEnable?: boolean;
  isForRequestToCancel?: boolean;
}) => {
  const {
    cart: cartData,
    addressList,
    isAddressListFetching,
    addShipping,
    editShippingEnabled,
    isButtonLoadingEnable,
    isForRequestToCancel,
  } = props;

  const { user } = useAuth();
  const { userDetail, iseRxDown } = useContext(UserContext);

  const [checkoutButtonLoading, setCheckoutButtonLoading] =
    useState<boolean>(false);
  const [modelButtonLoading, setModelButtonLoading] = useState<boolean>(false);
  //Form Inputs
  const [shippingCountry, setShippingCountry] = useState<string>('Australia');
  const [shippingFirstName, setShippingFirstName] = useState<string>(
    cartData?.shipping_address?.first_name,
  );
  const [shippingLastName, setShippingLastName] = useState<string>(
    cartData?.shipping_address?.last_name,
  );

  const [shippingAddress1, setShippingAddress1] = useState<string>(
    cartData?.shipping_address?.address1,
  );
  const [shippingAddress2, setShippingAddress2] = useState<
    string | null | undefined
  >(cartData?.shipping_address?.address2);
  const [shippingCity, setShippingCity] = useState<string>(
    cartData?.shipping_address?.city,
  );
  const [shippingProvince, setShippingProvince] = useState<string>(
    cartData?.shipping_address?.province,
  );
  const [shippingZip, setShippingZip] = useState<string>(
    cartData?.shipping_address?.zip,
  );

  const [specialDeliveryInstructions, setSpecialDeliveryInstructions] =
    useState<string>(cartData?.shipping_address?.company);

  const disabledFlag = useMemo(
    () => !editShippingEnabled,
    [editShippingEnabled, cartData],
  );

  const [modelAddressForDelete, setModelAddressForDelete] =
    useState<Record<string, string>>();

  const [addressData, setAddressData] = useState<Array<any>>([]);
  const [loadingEnable, setLoadingEnable] = useState<boolean>(false);

  const addShippingSubmit = async () => {
    if (!(shippingFirstName?.trim() && shippingLastName?.trim())) {
      showToast({
        message: 'Please enter a First and Last name',
        type: 'warning',
      });
      return;
    }

    if (shippingFirstName === 'undefined' || shippingLastName === 'undefined') {
      showToast({
        message: 'Please enter a First and Last name.',
        type: 'warning',
      });
      return;
    }

    setCheckoutButtonLoading(true);
    try {
      const shippingDetails = {
        country: shippingCountry,
        first_name: shippingFirstName,
        last_name: shippingLastName,
        address1: shippingAddress1,
        address2: shippingAddress2,
        city: shippingCity,
        province: shippingProvince,
        zip: shippingZip,
        company: specialDeliveryInstructions,
      };

      await addShipping(shippingDetails);
      setCheckoutButtonLoading(false);
      return true;
    } catch (error: any) {
      console.log(`Error occured while saving checkout ${error}`);
      setCheckoutButtonLoading(false);
      return false;
    }
  };

  const [selectedAddress, setSelectedAddress] = useState<number>(0);
  const autoFill = (address: ShopifyCustomerAddress) => {
    setShippingFirstName(address?.first_name);
    setShippingLastName(address?.last_name);
    setShippingAddress1(address?.address1);
    setShippingAddress2(address?.address2);
    setShippingCity(address?.city);
    setShippingProvince(address?.province);
    setShippingZip(address?.zip);
    setSpecialDeliveryInstructions(address?.company ?? '');
  };

  useEffect(() => {
    addressList.sort((a, b) => sort(a?.default, b?.default, false));
    setAddressData(addressList);
    const defaultAddressIndex = addressList.findIndex(
      (address) => address.default === true,
    );
    autoFill(addressList[defaultAddressIndex]);
    setSelectedAddress(defaultAddressIndex);
  }, []);

  const selectDeliveryAddress = (address: any, index: number) => {
    autoFill(address);
    setSelectedAddress(index);
  };

  useEffect(() => {
    if (isButtonLoadingEnable !== undefined) {
      setCheckoutButtonLoading(isButtonLoadingEnable);
    }
  }, [isButtonLoadingEnable]);

  const deleteAddress = async (addressId: number, customerId: number) => {
    setModelButtonLoading(true);
    setLoadingEnable(true);
    try {
      await deleteShippingAddress(
        addressId,
        customerId,
        user?.access_token as string,
      );
      const response = await callCustomerAddressesList();
      if (response) {
        setModelButtonLoading(false);
        setModelAddressForDelete(undefined);
        setSelectedAddress(0);
        autoFill(addressData[selectedAddress === 0 ? selectedAddress + 1 : 0]);
        setLoadingEnable(false);
        showToast({
          message: 'Address removed successfully',
          type: 'success',
        });
      }
    } catch (error) {
      setModelButtonLoading(false);
      showToast({
        message: 'Error removing address',
        type: 'error',
      });
      setLoadingEnable(false);
    }
  };

  const callCustomerAddressesList = async () => {
    try {
      if (!user?.access_token) return;
      const addressList = await callCustomerAddresses(user?.access_token);
      addressList.sort((a: any, b: any) => sort(a?.default, b?.default, false));
      setAddressData(addressList);
      return true;
    } catch (error) {
      setAddressData([]);
      return false;
    }
  };

  const customerAddressList = (
    <>
      {addressData?.map((address, index) => (
        <button
          className={`bg-white shadow p-3 rounded-md text-left active:border relative
          active:border-slate-600 ${selectedAddress === index ? 'border border-secondary-cornflowerblue-500' : ''}`}
          onClick={() => selectDeliveryAddress(address, index)}
          key={`address_${index}`}
        >
          {!address.default ? (
            <CloseIcon
              className="h-4 w-4 p-1 fill-gray-600 absolute top-0 right-0"
              onClick={() => setModelAddressForDelete(address)}
            />
          ) : null}
          <p className="line-clamp-3 text-sm leading-6 text-gray-600">
            <span className="font-semibold">{`${address?.first_name} ${address?.last_name}`}</span>
            {` - ${address?.address1}${fixNullAddressString(
              address?.address2,
            )}${fixNullAddressString(address?.city)}${fixNullAddressString(
              address?.province,
            )}${fixNullAddressString(address?.country)}`}
          </p>
        </button>
      ))}
    </>
  );

  const loadingAddressList = (
    <img className="top-1/2 left-1/2 transform" src={loading} alt="Loading" />
  );

  if (loadingEnable) return <Loading />;

  return (
    <>
      <CustomModal
        isOpen={modelAddressForDelete !== undefined}
        onClose={() => setModelAddressForDelete(undefined)}
        header={'Are you sure you want to remove this address?'}
        footer={
          <>
            <LoadingButton
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 !rounded !w-fit !font-normal !text-base yesButton1"
              isLoading={modelButtonLoading}
              buttonText={'Yes'}
              isDisabled={modelButtonLoading}
              onClick={() =>
                deleteAddress(
                  Number(modelAddressForDelete?.id),
                  Number(modelAddressForDelete?.customer_id),
                )
              }
            />
            <LoadingButton
              className="mr-4 px-4 py-2 bg-white !text-primary-blue-500 !rounded !w-fit !font-normal !text-base"
              isLoading={false}
              buttonText={'Cancel'}
              onClick={() => setModelAddressForDelete(undefined)}
            />
          </>
        }
        modalSize={'w-10/12 md:w-8/12 lg:w-4/12'}
      >
        <p className="line-clamp-3 text-sm leading-6 text-gray-600">
          <span className="font-semibold">{`${modelAddressForDelete?.first_name} ${modelAddressForDelete?.last_name}`}</span>
          {` - ${modelAddressForDelete?.address1}${fixNullAddressString(
            modelAddressForDelete?.address2,
          )}${fixNullAddressString(modelAddressForDelete?.city)}${fixNullAddressString(
            modelAddressForDelete?.province,
          )}${fixNullAddressString(modelAddressForDelete?.country)}`}
        </p>
      </CustomModal>

      {!disabledFlag &&
        !isForRequestToCancel &&
        (isAddressListFetching ? (
          <div className="w-full">{loadingAddressList}</div>
        ) : (
          <>
            <h3 className="text-sm xl:text-base font-semibold text-primary-blue-500 mb-3">
              Saved Addresses
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
              {customerAddressList}
            </div>
          </>
        ))}
      <form className="w-full">
        <div className="flex flex-wrap -mx-3 mb-6">
          <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
            <label
              className="block uppercase tracking-wide text-primary-blue-500 text-xs font-bold mb-2"
              htmlFor="first-name"
            >
              First Name
            </label>
            <input
              className="appearance-none block w-full bg-white shadow disabled:cursor-not-allowed 
              text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              id="first-name"
              type="text"
              value={shippingFirstName}
              onChange={(event) => setShippingFirstName(event.target.value)}
              disabled={disabledFlag}
            />
          </div>
          <div className="w-full md:w-1/2 px-3">
            <label
              className="block uppercase tracking-wide text-primary-blue-500 text-xs font-bold mb-2"
              htmlFor="last-name"
            >
              Last Name
            </label>
            <input
              className="appearance-none block w-full bg-white shadow disabled:cursor-not-allowed 
              text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="last-name"
              type="text"
              value={shippingLastName}
              onChange={(event) => setShippingLastName(event.target.value)}
              disabled={disabledFlag}
            />
          </div>
        </div>
        <AddressAutocomplete
          addressRefValue={shippingAddress1}
          address1ManualSetter={(address1) => {
            setShippingAddress1(address1);
          }}
          state={{
            city: shippingCity,
            country: shippingCountry,
            zip: shippingZip,
            state:
              states.find((x) => x.abbriviation === shippingProvince)?.name ||
              states.find((x) => x.name === shippingProvince)?.name ||
              shippingProvince,
            address2: shippingAddress2 ?? '',
            specialDeliveryInstructions,
            streetNumber: '',
            streetName: '',
            unit: '',
          }}
          setter={(data) => {
            setShippingCity(data.city);
            setShippingCountry(data.country);
            setShippingZip(data.zip);
            setShippingProvince(data.state);
            setShippingAddress2(data.address2 ?? '');
            setSpecialDeliveryInstructions(
              data?.specialDeliveryInstructions ?? '',
            );
            if (process.env.VITE_MAPS_AUTOCOMPLETE_KEY) {
              setShippingAddress1(
                data?.streetName ? getShippingAddress(data) : shippingAddress1,
              );
            }
          }}
          freezeAddressFields={disabledFlag}
          isForRequestToCancel={isForRequestToCancel}
        />
      </form>

      {!disabledFlag && (
        <div className="mt-2 w-full flex">
          <LoadingButton
            isLoading={checkoutButtonLoading}
            buttonText={
              cartData?.shipping_address?.address1
                ? 'Modify Shipping Details'
                : 'Add Shipping Details'
            }
            buttonIcon={<>{'🚚'}</>}
            isDisabled={
              cartData?.line_items.some(
                (l: { isProductOutOfStock: boolean }) => l.isProductOutOfStock,
              ) ||
              !userDetail?.supportingParams?.isUserAllowedToOrder ||
              iseRxDown
            }
            onClick={addShippingSubmit}
            className="w-full md:w-1/3 xl:w-max"
          />
        </div>
      )}
    </>
  );
};

export default ShippingDetails;
